import { useEffect, useRef } from "react";
import { logger } from "@/lib/logger";

/**
 * Silently sign the user out after `timeoutMs` of no user input.
 * Any of the listed events resets the timer. Timer stops when disabled=true.
 *
 * We keep the timeout ref in a `useRef` so re-renders don't clobber it, and
 * we only ever have ONE active timer per mount.
 */
export default function useIdleTimeout({ timeoutMs, onIdle, disabled = false }) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (disabled) return undefined;

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "visibilitychange"];

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      // Ignore hidden-tab wake-ups triggered by visibilitychange when the tab
      // becomes visible — we only want to *stop* being idle on real input.
      if (document.visibilityState === "hidden") return;
      timerRef.current = setTimeout(() => {
        try { onIdle && onIdle(); } catch (e) { logger.error("idle-timeout onIdle failed", e); }
      }, timeoutMs);
    };

    reset();
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [timeoutMs, onIdle, disabled]);
}
